<template>
  <v-navigation-drawer
    v-model="drawer"
    elevation="1"
    permanent
    :rail="rail"
    width="310"
    @click="rail = false"
  >
    <v-list>
      <v-list-item>
        <template #prepend>
          <v-avatar>
            <v-img
              v-if="avatarImageUrl"
              alt="User avatar"
              :src="avatarImageUrl"
            />
            <span v-else class="text-label-large">
              {{ displayInitials }}
            </span>
          </v-avatar>
        </template>
        <template #append>
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            @click.stop="rail = !rail"
          />
        </template>
        <v-list-item-title v-if="authStore.user">{{
          displayName
        }}</v-list-item-title>
        <v-list-item-subtitle>{{ roleLabel }}</v-list-item-subtitle>
      </v-list-item>
    </v-list>

    <v-divider />

    <v-list
      v-model:opened="openedGroupsState"
      density="compact"
      nav
      color="primary"
    >
      <nav-list-item
        v-for="(item, i) in compacted"
        :key="item.value ?? `i-${i}`"
        :item="item"
        :rail="rail"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
  import {
    SIDE_NAV_OPTIONS,
    useDevMode,
    usePermissionEvaluator,
    useXappCoreAuthAdapter,
    useXappCoreIdentityAdapter,
  } from '#xapp-core/foundation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  import {
    isRouteTargetMatch,
    resolveNavTarget,
    validateNavOptions,
  } from '../../helpers/navTarget'
  import NavListItem from '../NavListItem.vue'

  const props = defineProps({
    options: {
      default: () => SIDE_NAV_OPTIONS,
      type: Array,
    },
  })

  const authStore = useXappCoreAuthAdapter()
  const identityAdapter = useXappCoreIdentityAdapter()
  const { hasPermission } = usePermissionEvaluator()
  const { isDevMode } = useDevMode()
  const route = useRoute()

  const drawer = ref(true)
  const rail = ref(true)
  const openedGroupsState = ref([])

  const displayInitials = computed(() => {
    return identityAdapter.getInitials(authStore.user)
  })

  const displayName = computed(() => {
    return identityAdapter.getDisplayName(authStore.user)
  })

  const roleLabel = computed(() => {
    return identityAdapter.getRoleLabel(authStore.user)
  })

  const avatarImageUrl = computed(() => {
    const source = identityAdapter.getAvatarSource(authStore.user)
    return identityAdapter.resolveAvatarUrl(source)
  })

  // Nav helper functions
  function hasVisibleChildren(item) {
    const kids = Array.isArray(item.children) ? item.children : []
    return kids.some((element) => hasPermission(element))
  }

  function isVisible(item) {
    if (item.type === 'divider' || item.type === 'subheader') return true // handled by compactor
    if (Array.isArray(item.children) && item.children.length > 0) {
      return hasPermission(item) && hasVisibleChildren(item)
    }
    return hasPermission(item)
  }

  const compacted = computed(() => {
    const out = []
    let pendingHeader = null

    for (const item of props.options) {
      // If it's a developer option, only show it when dev mode is on
      if (item.developerOnly && item.developerOnly && !isDevMode.value) continue

      if (item.type === 'subheader') {
        // hold the header; only emit it if we later see a visible item
        pendingHeader = item
        continue
      }

      if (item.type === 'divider') {
        // close any pending header with no items (drop it)
        pendingHeader = null
        // avoid duplicate/trailing dividers
        if (out.at(-1)?.type !== 'divider') out.push(item)
        continue
      }

      // normal leaf or group
      if (!isVisible(item)) continue

      // if we have a pending header, emit it now (it has at least one visible item)
      if (pendingHeader) {
        out.push(pendingHeader)
        pendingHeader = null
      }

      out.push(item)
    }

    // drop trailing divider if present
    if (out.at(-1)?.type === 'divider') out.pop()

    return out
  })

  const activeGroupValue = computed(() => {
    const activeGroup = compacted.value.find((item) => {
      const children = Array.isArray(item.children) ? item.children : []
      if (children.length === 0) return false
      return children.some((child) => {
        if (!hasPermission(child)) return false
        const target = resolveNavTarget(child)
        return isRouteTargetMatch(route, target)
      })
    })
    return activeGroup?.value || ''
  })

  function syncOpenedGroupsWithState() {
    if (rail.value) {
      openedGroupsState.value = []
      return
    }
    openedGroupsState.value = activeGroupValue.value
      ? [activeGroupValue.value]
      : []
  }

  watch(rail, () => {
    syncOpenedGroupsWithState()
  })

  watch(activeGroupValue, () => {
    syncOpenedGroupsWithState()
  })

  onMounted(() => {
    if (import.meta.env.DEV) {
      const issues = validateNavOptions(props.options)
      if (issues.length > 0) {
        console.warn('[xapp-core] Invalid side-nav options detected:', issues)
      }
    }
    syncOpenedGroupsWithState()
  })
</script>

<style>
  .custom-icon-color .v-list-item__prepend {
    color: red !important;
  }
  .v-list .v-divider[role='separator'] {
    margin-top: 8px;
    margin-bottom: 8px;
  }
</style>
